// Client-side mirror of the gateway's templateRenderer.js — used for live preview and
// credit estimates on the personalized-SMS page. The backend is authoritative at send time.
// Placeholders are case-insensitive, allow surrounding whitespace ({{ name }}), and a missing
// variable renders as an empty string (never the literal "{{name}}").

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

function coerce(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

export function renderTemplate(template: string, vars: Record<string, unknown>): string {
  if (!template) return "";
  if (!template.includes("{{")) return template;
  const map = new Map<string, unknown>();
  for (const [k, v] of Object.entries(vars || {})) map.set(k.toLowerCase(), v);
  return template.replace(PLACEHOLDER_RE, (_m, key: string) => coerce(map.get(key.toLowerCase())));
}

export function extractPlaceholders(template: string): string[] {
  if (!template) return [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(template)) !== null) seen.add(m[1].toLowerCase());
  return [...seen];
}

// Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes (""), commas and
// newlines inside quotes, and CRLF. Good enough for recipient uploads (not arbitrary CSV).
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  const s = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\n") {
      row.push(field); field = "";
      rows.push(row); row = [];
    } else {
      field += c;
    }
  }
  // trailing field/row (no final newline)
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  // drop fully-empty rows
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}
